/**
 * SchoolContent
 * 
 * @Author: Focci
 * @Date: 2023-08-09 11:36:27
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-09 11:36:27
 */

'use client'

import PropTypes from 'prop-types'
import Link from 'next/link'
import Table from '@comp/table'
import Folding from '@comp/Folding'
import { useLocale, useTranslations } from 'next-intl'
import { schoolDetail } from '@lib/route/house'
import { isAU, toSlug } from '@lib/utils'

function Span({ children, tag = 'span', ...tagProps }) {
  const Tag = tag
  return (
    <Tag className="text-f.7 leading-none" {...tagProps}>
      {children}
    </Tag>
  )
}

function getColumns({ tfa, th, tse, tsc, tpr, locale }) {
  const tmp = [
    {
      key: 'name',
      label: tfa('name'),
      headBackgroundColor: '#f8f8f8',
      width: 120,
      render: ({ row }) => (<Span tag="b">{row.name}</Span>)
    },
    {
      key: 'distance',
      label: th('distance'),
      sortable: true,
      width: 70,
      headBackgroundColor: '#f8f8f8',
      render: ({ row }) => (
        <Span>
          {
            row.distance !== undefined
              ? `${(row.distance).toFixed(2)} km`
              : '-'
          }
        </Span>
      )
    },
    {
      key: 'school_type',
      label: tpr('type'),
      headBackgroundColor: '#f8f8f8',
      width: 75,
      render: ({ row }) => (<Span>{row.school_type}</Span>)
    },
    {
      key: 'year_range',
      label: tse('level'),
      headBackgroundColor: '#f8f8f8',
      width: 60,
      render: ({ row }) => (<Span>{row.year_range || '-'}</Span>)
    },
    {
      key: 'authority',
      label: tsc('attribute'),
      headBackgroundColor: '#f8f8f8',
      width: 80,
      render: ({ row }) => (<Span>{row.authority || '-'}</Span>)
    },
    {
      key: 'gender',
      label: tse('gender'),
      width: 80,
      headBackgroundColor: '#f8f8f8',
      render: ({ row }) => (<Span>{row.gender}</Span>)
    },
    {
      key: 'score',
      label: 'Score',
      width: 75,
      headBackgroundColor: '#f8f8f8',
      sortable: true,
      render: ({ row }) => (
        row.score_type && row.score
          ? (<Span>{`${row.score_type}: ${row.score}`}</Span>)
          : '-'
      )
    },
  ]

  if(!isAU) {
    tmp.push({
      key: 'decile',
      label: 'Decile',
      width: 45,
      headBackgroundColor: '#f8f8f8',
      sortable: true,
      render: ({ row }) => ((row.decile && row.decile !== '0') ? row.decile : '-')
    })
  }

  tmp.push({
    key: 'slug',
    width: 70,
    headBackgroundColor: '#f8f8f8',
    align: 'right',
    render: ({ row }) => {
      const slug = row.slug || toSlug(row.name)
      const pathname = `${slug}-${row.id}`
      
      return (
        <Link href={schoolDetail(pathname, locale)} className="text-primary">
          <Span>{th('detail')}</Span>
        </Link>
      )
    }
  })

  return tmp
}

export default function SchoolContent({
  data = []
}) {
  const locale = useLocale()
  const tfa = useTranslations('favorite')
  const th = useTranslations('house')
  const tse = useTranslations('search')
  const tsc = useTranslations('school')
  const tpr = useTranslations('productspec')
  const columns = getColumns({ tfa, th, tse, tsc, tpr, locale })
  
  return (
    <Folding 
      triggerClassName="flex justify-center md:justify-start"
      height={450}
    >
      <Table
        bordered
        data={data}
        columns={columns}
        defaultOrderProp="distance"
      />
    </Folding>
  )
}

SchoolContent.propTypes = {
  data: PropTypes.array,
}
